import React from 'react';
import toast, { Toaster, ToastBar } from 'react-hot-toast';

// Toast notification service
// eslint-disable-next-line react-refresh/only-export-components
export const notify = {
  success: (message: string) => toast.success(message),
  error: (message: string) => toast.error(message),
  loading: (message: string) => toast.loading(message),
  custom: (message: string) => toast(message),
  promise: <T,>(
    promise: Promise<T>,
    msgs: {
      loading: string;
      success: string;
      error: string;
    }
  ) => toast.promise(promise, msgs),
  dismiss: (toastId?: string) => toast.dismiss(toastId),
  remove: (toastId?: string) => toast.remove(toastId),
};

// Toast container component
const ProgressBar: React.FC<{ duration: number; active: boolean; color?: string }> = ({ duration, active, color }) => {
  const [width, setWidth] = React.useState(0);
  React.useEffect(() => {
    if (!active || duration === Infinity) return;
    const r = requestAnimationFrame(() => setWidth(100));
    return () => cancelAnimationFrame(r);
  }, [active, duration]);
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        width: '100%',
        height: 4,
        background: 'rgba(255,255,255,0.25)',
      }}
    >
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          height: '100%',
          width: `${width}%`,
          transition: `width ${duration}ms linear`,
          background: color ?? 'rgba(255,255,255,0.9)',
        }}
      />
    </div>
  );
};

export const ToastContainer: React.FC = () => (
  <Toaster
    position="top-right"
    reverseOrder={false}
    gutter={8}
    containerClassName=""
    containerStyle={{}}
    toastOptions={{
      className: '',
      duration: 4000,
      style: {
        background: '#363636',
        color: '#fff',
      },
      success: {
        duration: 3000,
        style: {
          background: '#4caf50',
          color: '#fff',
          border: '1px solid #45a049',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#4caf50',
        },
      },
      error: {
        duration: 5000,
        style: {
          background: '#f44336',
          color: '#fff',
          border: '1px solid #d32f2f',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#f44336',
        },
      },
      loading: {
        duration: Infinity,
      },
    }}
  >
    {(t) => {
      const baseDuration = 4000;
      const duration =
        typeof (t as any).duration === 'number'
          ? (t as any).duration
          : t.type === 'success'
          ? 3000
          : t.type === 'error'
          ? 5000
          : t.type === 'loading'
          ? Infinity
          : baseDuration;
      const barColor = t.type === 'success' ? 'rgba(255,255,255,0.9)' : t.type === 'error' ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.9)';
      return (
        <ToastBar
          toast={t}
          style={{
            ...t.style,
            position: 'relative',
            paddingRight: 8,
          }}
        >
          {({ icon, message }) => (
            <>
              {icon}
              {message}
              <button
                onClick={() => toast.dismiss(t.id)}
                style={{
                  marginLeft: 12,
                  background: 'transparent',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                }}
                aria-label="Close"
              >
                ×
              </button>
              <ProgressBar duration={duration} active={t.visible} color={barColor} />
            </>
          )}
        </ToastBar>
      );
    }}
  </Toaster>
);
